import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { mergeOptions, type DesignKey, type TemplateOptions } from './template-options';
import { buildClassicDoc } from './designs/classic-design';
import { buildModernDoc } from './designs/modern-design';
import { buildMinimalDoc } from './designs/minimal-design';
import type { DesignCtx, RenderData } from './designs/design-utils';
import type { PdfCompanyInfo } from './types';

// pdfmake 0.3.x: vfs_fonts.js exports the vfs dictionary as the default
// export AND auto-installs via addVirtualFileSystem when loaded. Handle both.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any)?.vfs ?? pdfFonts;

export type { RenderData } from './designs/design-utils';

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2500);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function buildFromDesign(
  designKey: DesignKey,
  ctx: DesignCtx,
): TDocumentDefinitions {
  switch (designKey) {
    case 'modern': return buildModernDoc(ctx);
    case 'minimal': return buildMinimalDoc(ctx);
    case 'classic':
    default: return buildClassicDoc(ctx);
  }
}

export interface GenerateInput {
  designKey: DesignKey;
  options: Partial<TemplateOptions>;
  company: PdfCompanyInfo;
  data: RenderData;
  docTypeTitle: string;
}

function safePdfFilename(parts: Array<string | null | undefined>): string {
  const base = parts
    .filter((part): part is string => !!part)
    .join('-')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${base || 'document'}.pdf`;
}

interface BrowserPdfDocument {
  download(filename?: string): Promise<void> | void;
  print(win?: Window | null): Promise<void>;
}

function createPrintFrame(filename: string): HTMLIFrameElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const iframe = document.createElement('iframe');
  iframe.title = filename;
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);

  return iframe;
}

async function openPdf(def: TDocumentDefinitions, filename: string): Promise<void> {
  const pdf = pdfMake.createPdf(def) as BrowserPdfDocument;
  const iframe = createPrintFrame(filename);
  if (!iframe?.contentWindow) {
    await pdf.download(filename);
    return;
  }

  try {
    await pdf.print(iframe.contentWindow);
    setTimeout(() => iframe.remove(), 60_000);
  } catch {
    iframe.remove();
    await pdf.download(filename);
  }
}

export async function generatePdf({ designKey, options, company, data, docTypeTitle }: GenerateInput): Promise<void> {
  const filename = safePdfFilename([docTypeTitle, data.number]);
  const merged = mergeOptions(options);
  const logoDataUrl = merged.logoPosition !== 'hidden' && company.logoUrl
    ? await fetchImageAsDataUrl(company.logoUrl)
    : null;
  const ctx: DesignCtx = { options: merged, company, data, docTypeTitle, logoDataUrl };
  const def = buildFromDesign(designKey, ctx);
  await openPdf(def, filename);
}

/**
 * Generate a single combined PDF from multiple documents and open it.
 * Each sub-document starts on a new page. Shared chrome (page size, margins,
 * footer, background) comes from the first item's template.
 */
export async function generatePdfBatch(items: GenerateInput[]): Promise<void> {
  if (items.length === 0) return;
  if (items.length === 1) return generatePdf(items[0]!);

  const filename = safePdfFilename([items[0]!.docTypeTitle, 'selection']);
  const subDefs: TDocumentDefinitions[] = [];
  for (const input of items) {
    const merged = mergeOptions(input.options);
    const logoDataUrl = merged.logoPosition !== 'hidden' && input.company.logoUrl
      ? await fetchImageAsDataUrl(input.company.logoUrl)
      : null;
    const ctx: DesignCtx = {
      options: merged,
      company: input.company,
      data: input.data,
      docTypeTitle: input.docTypeTitle,
      logoDataUrl,
    };
    subDefs.push(buildFromDesign(input.designKey, ctx));
  }

  const shell = subDefs[0]!;
  const combinedContent: Content[] = [];
  for (let i = 0; i < subDefs.length; i++) {
    const raw = subDefs[i]!.content;
    const content = Array.isArray(raw) ? raw : [raw];
    if (i === 0) {
      combinedContent.push(...(content as Content[]));
    } else {
      combinedContent.push({ stack: content as Content[], pageBreak: 'before' });
    }
  }

  const def: TDocumentDefinitions = { ...shell, content: combinedContent };
  await openPdf(def, filename);
}

/**
 * Generate a PDF and return a blob URL (for embedding in an iframe as a live
 * preview inside the editor). Caller is responsible for revoking the URL.
 */
export async function generatePdfDataUrl({ designKey, options, company, data, docTypeTitle }: GenerateInput): Promise<string> {
  const merged = mergeOptions(options);
  const logoDataUrl = merged.logoPosition !== 'hidden' && company.logoUrl
    ? await fetchImageAsDataUrl(company.logoUrl)
    : null;
  const ctx: DesignCtx = { options: merged, company, data, docTypeTitle, logoDataUrl };
  const def = buildFromDesign(designKey, ctx);
  return new Promise<string>((resolve, reject) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (pdfMake.createPdf(def) as any).getBlob((blob: Blob) => {
        resolve(URL.createObjectURL(blob));
      });
    } catch (err) {
      reject(err);
    }
  });
}
